import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { PermissionsUtil } from '../../../utils/permissions/permissionsUtil';
import { PERMISSIONS } from '../../../constants/permissions/Permissions.constants';
import { useContext } from 'react';
import SettingsContext from '../../../contexts/settings.context';
import { useGetTermSetQuery } from '../../../services/i18n/i18n.service';
import { SUPPLY_CHAIN_DEFS } from '../../../constants/i18n/translations/termSetDefinitions/supply-chain';
import { skipToken } from '@reduxjs/toolkit/query';
import { RoutingValues } from '../../../types/Shared.types';
import {
    useCreateSupplierTypeMutation,
    useDeleteSupplierTypeMutation,
    useGetSupplierTypeQuery,
    useUpdateSupplierTypeMutation,
} from '../../../services/supplier/supplier.service';
import { SupplierType } from '../../../types/Supplier.types';
import useBaseForm from '../../form/hooks/useBaseForm';

const useSupplierTypeForm = (id: string) => {
    const sortOrderState = useSelector((state: RootState) => state.suppliers);

    const canCreate = PermissionsUtil.hasPermission(
        PERMISSIONS.SUPPLIERS.SETUP.SUPPLIER_TYPES.CREATE
    );

    const canUpdate = PermissionsUtil.hasPermission(
        PERMISSIONS.SUPPLIERS.SETUP.SUPPLIER_TYPES.EDIT
    );

    const canDelete = PermissionsUtil.hasPermission(
        PERMISSIONS.SUPPLIERS.SETUP.SUPPLIER_TYPES.DELETE
    );

    const { settings } = useContext(SettingsContext);
    const { data: termSet } = useGetTermSetQuery(
        settings?.userSettings
            ? {
                  languageId: settings?.userSettings?.languageId,
                  code: SUPPLY_CHAIN_DEFS.SUPPLIER_TYPE_FORM,
              }
            : skipToken
    );

    const { data, isLoading } = useGetSupplierTypeQuery(
        id === RoutingValues.newId ? skipToken : id
    );

    const [createMutation] = useCreateSupplierTypeMutation();
    const [updateMutation] = useUpdateSupplierTypeMutation();
    const [deleteMutation] = useDeleteSupplierTypeMutation();

    const blankSupplierType: SupplierType = {
        id: null,
        businessEntityId: null,
        typeActive: true,
        default: false,
        emergencyResponse: false,
        createdAt: null,
        createdBy: null,
        updatedAt: null,
        updatedBy: null,
        sortOrder: sortOrderState.sortOrderSet.maxValue,
        code: '',
        description: '',
    };

    const {
        fields,
        handleFieldChange,
        formMethods,
        onCreate,
        onUpdate,
        onDelete,
        closeForm,
        auditableFields,
    } = useBaseForm({
        closePath: '/suppliers/setup/type',
        blankEntity: blankSupplierType,
        activeEntity: data,
        getDescription: () => {
            return `Supplier Type ${fields.code}`;
        },
        createEntity: () => {
            return createMutation({ postBody: fields });
        },
        updateEntity: () => {
            return updateMutation({
                id: id as unknown as number,
                postBody: fields,
            });
        },
        deleteEntity: () => {
            return deleteMutation(id);
        },
    });

    return {
        fields,
        handleFieldChange,
        formMethods,
        onCreate,
        onUpdate,
        onDelete,
        closeForm,
        isLoading,
        termSet,
        canCreate,
        canUpdate,
        canDelete,
        data,
        auditableFields,
    };
};

export default useSupplierTypeForm;
